import React, { useState, useEffect, useCallback } from 'react'
import { Navigate, useLocation } from 'react-router-dom'
import { CSpinner } from '@coreui/react'
import api from '../utils/apiClient'

const ProtectedRoute = ({ children }) => {
  const [isVerified, setIsVerified] = useState(null)
  const [loading, setLoading] = useState(true)
  const [hasCheckedStatus, setHasCheckedStatus] = useState(false)
  const location = useLocation()
  // ✅ SECURITY: Vérification basée sur userId (pas de token JS)
  // La vraie validation se fait côté serveur via le cookie httpOnly
  const userId = localStorage.getItem('userId')

  // ✅ NOUVEAU : États pour l'onboarding admin
  const [isAdmin, setIsAdmin] = useState(false)
  const [adminOnboardingDone, setAdminOnboardingDone] = useState(null)

  // Vérifier si l'utilisateur doit changer son mot de passe temporaire
  const hasTempPassword = localStorage.getItem('tempPassword') === 'true'

  // Vérification d'accès
  const checkJobAccess = () => {
    // Fonction inchangée
    return true
  }

  if (!checkJobAccess()) {
    return <Navigate to="/jobs" replace />
  }

  const checkProfileStatus = useCallback(async () => {
    if (hasCheckedStatus) return

    try {
      const response = await api.get('/members/profile-status')
      setIsVerified(response.isProfileVerified)

      // ✅ NOUVEAU : Récupérer le rôle depuis la réponse ou le token
      const userRole = response.role || localStorage.getItem('userRole')
      const userIsAdmin = userRole === 'admin'
      setIsAdmin(userIsAdmin)

      // ✅ NOUVEAU : Si admin, vérifier l'onboarding admin
      if (userIsAdmin) {
        try {
          const onboardingResponse = await api.get('/organization/admin-onboarding-status')
          setAdminOnboardingDone(onboardingResponse.onboardingComplete)
        } catch (onboardingError) {
          console.warn('Erreur vérification onboarding admin:', onboardingError)
          // En cas d'erreur, on considère l'onboarding comme fait pour ne pas bloquer
          setAdminOnboardingDone(true)
        }
      } else {
        // Non-admin : pas d'onboarding admin
        setAdminOnboardingDone(true)
      }

      setHasCheckedStatus(true)
    } catch (error) {
      console.error('Erreur vérification profil:', error.response || error)

      if (error.response?.status === 401) {
        // ✅ SECURITY: Nettoyer localStorage (préserver données admin)
        const adminUser = localStorage.getItem('adminUser')
        const adminUserId = localStorage.getItem('adminUserId')
        const lang = localStorage.getItem('terrilink_lang')
        localStorage.clear()
        if (adminUser) localStorage.setItem('adminUser', adminUser)
        if (adminUserId) localStorage.setItem('adminUserId', adminUserId)
        if (lang) localStorage.setItem('terrilink_lang', lang)
        window.location.replace('/login')
      } else {
        setIsVerified(false)
        setHasCheckedStatus(true)
      }
    } finally {
      setLoading(false)
    }
  }, [userId, hasCheckedStatus])

  // [AJOUT] Quand on reçoit "auth_updated", on relance la vérification
  // Dans la fonction useEffect qui gère l'écouteur 'auth_updated'
  useEffect(() => {
    function handleAuthUpdate() {
      console.log('🛡️ ProtectedRoute: auth_updated reçu')
      console.log('🛡️ État actuel - hasCheckedStatus:', hasCheckedStatus, 'isVerified:', isVerified)

      // ✅ SECURITY: Plus de token - vérifier uniquement userId
      const storedUserId = localStorage.getItem('userId')
      console.log('🛡️ Valeurs localStorage:', { userId: storedUserId })

      setHasCheckedStatus(false)
      setLoading(true)

      console.log('🛡️ Lancement de checkProfileStatus')
    }

    document.addEventListener('auth_updated', handleAuthUpdate)

    return () => {
      document.removeEventListener('auth_updated', handleAuthUpdate)
    }
  }, [])

  useEffect(() => {
    // ✅ SECURITY: Vérifier via userId (pas de token JS)
    if (userId && !hasCheckedStatus) {
      checkProfileStatus()
    } else {
      setLoading(false)
    }
  }, [userId, checkProfileStatus, hasCheckedStatus])

  // Afficher le spinner pendant le chargement
  if (loading) {
    return (
      <div className="vh-100 d-flex justify-content-center align-items-center">
        <CSpinner />
      </div>
    )
  }

  // ✅ SECURITY: Redirection vers login si pas de userId
  // (la vraie validation se fait côté serveur via cookie httpOnly)
  if (!userId) {
    return <Navigate to="/login" state={{ from: location }} replace />
  }

  // Redirection vers change-password si nécessaire
  if (hasTempPassword && location.pathname !== '/change-password') {
    return <Navigate to="/change-password" replace />
  }

  // ✅ NOUVEAU : Redirection vers onboarding admin si nécessaire (AVANT verify-profile)
  // Uniquement pour les admins avec onboarding non terminé
  if (
    !hasTempPassword &&
    isAdmin &&
    adminOnboardingDone === false &&
    location.pathname !== '/admin-onboarding'
  ) {
    return <Navigate to="/admin-onboarding" replace />
  }

  // Redirection depuis admin-onboarding si déjà terminé
  if (adminOnboardingDone === true && location.pathname === '/admin-onboarding') {
    return <Navigate to="/verify-profile" replace />
  }

  // Redirection vers verify-profile si nécessaire
  if (!hasTempPassword && isVerified === false && location.pathname !== '/verify-profile' && location.pathname !== '/admin-onboarding') {
    return <Navigate to="/verify-profile" replace />
  }

  // Redirection vers dashboard si déjà vérifié
  if (isVerified === true && location.pathname === '/verify-profile') {
    return <Navigate to="/publications" replace />
  }

  return React.isValidElement(children)
    ? React.cloneElement(children, { onVerificationSuccess: checkProfileStatus })
    : children
}

export default ProtectedRoute
